<script setup lang="ts">
import { computed } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { Roles } from '@/constants.ts'

const props = defineProps<{ role: Roles | null }>()

const route = useRoute()
const { t } = useI18n()

interface NavItem {
  label: string
  icon: string
  to: string
  // exact — активная подсветка только на точном совпадении пути (без префикс-матча),
  // чтобы вложенные подразделы (/events/gallery) не подсвечивали родительский пункт (/events).
  exact?: boolean
}

interface NavSection {
  label: string
  items: NavItem[]
}

// Меню сгруппировано по доменам (паттерн career-guidance: раздел = домен, пункты = подразделы).
const sections = computed<NavSection[]>(() => {
  const result: NavSection[] = [
    {
      label: t('nav.sectionMain'),
      items: [
        { label: t('nav.dashboard'), icon: 'pi pi-home', to: '/', exact: true },
        { label: t('nav.analytics'), icon: 'pi pi-chart-bar', to: '/analytics' },
      ],
    },
    {
      // Мероприятия — домен события: реестр (просмотр/ЖЦ), галерея фото и протокол результатов.
      // Admin и Manager (allowedRoles подразделов). Реестр — exact, иначе подразделы подсветят его.
      label: t('nav.sectionEvents'),
      items: [
        { label: t('nav.events'), icon: 'pi pi-calendar', to: '/events', exact: true },
        { label: t('nav.gallery'), icon: 'pi pi-images', to: '/events/gallery' },
        { label: t('nav.results'), icon: 'pi pi-flag', to: '/events/results' },
      ],
    },
    {
      // Воронка участия: ростер участников, заявки и наблюдаемость отвала на шаге OTP. Admin и Manager.
      label: t('nav.sectionRegistrations'),
      items: [
        { label: t('nav.participants'), icon: 'pi pi-id-card', to: '/participants' },
        { label: t('nav.registrations'), icon: 'pi pi-users', to: '/registrations' },
        { label: t('nav.pendingOtp'), icon: 'pi pi-hourglass', to: '/pending-otp' },
        { label: t('nav.bibs'), icon: 'pi pi-hashtag', to: '/bibs' },
      ],
    },
    {
      // Исходящие рассылки + мониторинг доставки (Invitation/TeamInvite/OTP, Postmark). Admin и Manager.
      label: t('nav.sectionCommunications'),
      items: [
        { label: t('nav.invitations'), icon: 'pi pi-send', to: '/invitations' },
        { label: t('nav.sms'), icon: 'pi pi-comments', to: '/sms' },
        { label: t('nav.email'), icon: 'pi pi-envelope', to: '/email' },
        { label: t('nav.subscriptions'), icon: 'pi pi-bell', to: '/subscriptions' },
      ],
    },
    {
      // Раздел «Безопасность»: антивирус-проверки загруженных документов (ClamAV) и
      // блокировки входа по OTP (перебор кода). Admin и Manager.
      label: t('nav.sectionSecurity'),
      items: [
        { label: t('nav.securityDocuments'), icon: 'pi pi-file', to: '/documents/security' },
        { label: t('nav.otpLockouts'), icon: 'pi pi-ban', to: '/otp-lockouts' },
      ],
    },
  ]

  // Настройки — раздел только для админа (Manager заявки видит, персонал/фичи — нет).
  // Переключение фич = деплой-решение (включить/выключить поведение на проде), поэтому здесь.
  if (props.role === Roles.Admin) {
    result.push({
      label: t('nav.sectionSettings'),
      items: [
        { label: t('nav.staff'), icon: 'pi pi-shield', to: '/staff' },
        { label: t('nav.featureFlags'), icon: 'pi pi-flag', to: '/feature-flags' },
      ],
    })
  }

  return result
})

function isActive(item: NavItem): boolean {
  if (item.exact) return route.path === item.to
  return route.path === item.to || route.path.startsWith(item.to + '/')
}
</script>

<template>
  <aside class="layout-sidebar">
    <nav class="layout-menu">
      <template v-for="section in sections" :key="section.label">
        <div class="layout-menu-section">{{ section.label }}</div>
        <RouterLink
          v-for="item in section.items"
          :key="item.to"
          :to="item.to"
          class="layout-menu-item"
          active-class=""
          exact-active-class=""
          :class="{ 'router-link-active': isActive(item) }"
        >
          <i :class="['layout-menu-item-icon', item.icon]" />
          <span class="layout-menu-item-label">{{ item.label }}</span>
        </RouterLink>
      </template>
    </nav>
  </aside>
</template>
